import React, { useState } from "react";
import {
  Card,
  Button,
  Input,
  Radio,
  InputNumber,
  Row,
  Col,
  Typography,
  Space,
  Modal,
  Select,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import "./FormBuilder.css";
import dataService from "../networks/api/service/dataService";
import colors from "../assets/styles/colour";

const { TextArea } = Input;
const { Title } = Typography;

const FormBuilder = () => {
  const [formName, setFormName] = useState(""); // Form name state
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    type: "text",
    question: "",
    options: [],
    answer: "",
  });
  const [visible, setVisible] = useState(false);

  // Handle question type change
  const handleQuestionTypeChange = (value) => {
    setCurrentQuestion({ ...currentQuestion, type: value });
  };

  // Handle form name change
  const handleFormNameChange = (e) => {
    setFormName(e.target.value);
  };

  // Handle question text change
  const handleQuestionChange = (e) => {
    setCurrentQuestion({ ...currentQuestion, question: e.target.value });
  };

  // Handle options change for multiple choice questions
  const handleOptionChange = (e, index) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = e.target.value;
    setCurrentQuestion({ ...currentQuestion, options: newOptions });
  };

  // Add a new option for the multiple choice question
  // const addOption = () => {
  //   // Ensure there is at least one option filled before adding a new one
  //   const isAnyOptionFilled = currentQuestion.options.some(
  //     (option) => option !== ""
  //   );
  //   if (!isAnyOptionFilled) {
  //     Modal.warning({
  //       title: "Error",
  //       content: "Please fill in at least one option before adding a new one.",
  //     });
  //     return;
  //   }
  //   setCurrentQuestion({
  //     ...currentQuestion,
  //     options: [...currentQuestion.options, ""],
  //   });
  // };

  const addOption = () => {
    // Allow adding the first option without validation
    if (currentQuestion.options.length === 0) {
      setCurrentQuestion({
        ...currentQuestion,
        options: [...currentQuestion.options, ""],
      });
      return;
    }

    // Ensure there is at least one option filled before adding a new one (for subsequent options)
    const isAnyOptionFilled = currentQuestion.options.some(
      (option) => option !== ""
    );
    if (!isAnyOptionFilled) {
      Modal.warning({
        title: "Error",
        content: "Please fill in at least one option before adding a new one.",
      });
      return;
    }

    // Add the new option if the validation passes
    setCurrentQuestion({
      ...currentQuestion,
      options: [...currentQuestion.options, ""],
    });
  };

  // Add a new question to the form
  const addQuestion = () => {
    if (!formName) {
      Modal.warning({
        title: "Error",
        content: "Please enter a survey name.",
      });
      return;
    }

    const isOptionEmpty = currentQuestion.options.some((val) => {
      if (val == "") {
        return true;
      } else {
        return false;
      }
    });

    if (isOptionEmpty) {
      Modal.warning({
        title: "Error",
        content: "Options cant be empty",
      });
      return;
    }

    if (currentQuestion.question === "") {
      Modal.warning({
        title: "Error",
        content: "Please enter a question before adding it.",
      });
      return;
    }

    setQuestions([...questions, currentQuestion]);
    setCurrentQuestion({
      type: "text",
      question: "",
      options: [],
      answer: "",
    });
  };

  // Delete a question from the form
  const deleteQuestion = (index) => {
    const newQuestions = [...questions];
    newQuestions.splice(index, 1);
    setQuestions(newQuestions);
  };

  // Render the question based on type
  const renderQuestion = (question, index) => {
    switch (question.type) {
      case "text":
        return (
          <div key={index} className="question-container">
            <div className="question-header">
              <label>{question.question}</label>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteQuestion(index)}
                className="delete-btn"
              />
            </div>
            <Input
              value={question.answer || ""}
              placeholder="Your answer here"
              className="question-input"
              disabled // Make input non-editable
            />
          </div>
        );
  
      case "radio":
        return (
          <div key={index} className="question-container">
            <div className="question-header">
              <label>{question.question}</label>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteQuestion(index)}
                className="delete-btn"
              />
            </div>
            <Radio.Group value={question.answer} className="radio-group" disabled>
              {question.options.map((option, idx) => (
                <Radio key={idx} value={option} className="radio-option">
                  {option}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        );
  
      case "number":
        return (
          <div key={index} className="question-container">
            <div className="question-header">
              <label>{question.question}</label>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteQuestion(index)}
                className="delete-btn"
              />
            </div>
            <InputNumber
              min={0}
              value={question.answer || ""}
              placeholder="Enter a number"
              className="number-input"
              disabled // Disable input
            />
          </div>
        );
  
      case "comment":
        return (
          <div key={index} className="question-container">
            <div className="question-header">
              <label>{question.question}</label>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteQuestion(index)}
                className="delete-btn"
              />
            </div>
            <TextArea
              rows={4}
              value={question.answer || ""}
              placeholder="Your suggestions here"
              className="comment-input"
              disabled // Disable input
            />
          </div>
        );
  
      case "rating":
        return (
          <div key={index} className="question-container">
            <div className="question-header">
              <label>{question.question}</label>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteQuestion(index)}
                className="delete-btn"
              />
            </div>
            <Space>
              {[...Array(5)].map((_, idx) => (
                <Button
                  key={idx}
                  disabled // Disable rating selection
                  className={`rating-btn ${
                    idx < question.answer ? "selected" : ""
                  }`}
                >
                  {idx + 1}
                </Button>
              ))}
            </Space>
          </div>
        );
  
      default:
        return null;
    }
  };
  

  // Handle the form submission
  const handleFormSubmit = () => {
    if (!formName) {
      Modal.warning({
        title: "Error",
        content: "Please enter a survey name.",
      });
      return;
    }

    // console.log("Form Data Submitted", { formName, questions });
    // console.log([
    //   {
    //     type: "text",
    //     question: "email",

    //     options: [],
    //     answer: "",
    //   },
    //   ...questions,
    // ]);
    const hasEmail = questions.some((val) => {
      if (val.question.toLowerCase() === "email") {
        return true;
      } else {
        return false;
      }
    });
    console.log(hasEmail, "hasEmail");
    const payload = {
      formName,
      questions: hasEmail
        ? questions
        : [
            {
              type: "email",
              question: "email",
              options: [],
              answer: "",
            },
            ...questions,
          ],
    };
    createForm(payload);
    console.log(payload, "payload");
    // Modal.success({
    //   title: "Form Submitted Successfully",
    //   content: "Your survey data has been successfully saved.",
    // });
  };

  const createForm = async (payload) => {
    console.log(payload, "payload", currentQuestion);

    try {
      const response = await dataService.createForm(payload);
      if (response.code === 200) {
        setQuestions([]);
        setFormName("");
        Modal.success({
          title: "Form Created Successfully",
          content: "Your survey Form has been Created Successfully.",
        });
      }
    } catch (e) {}
  };

  return (
    <div className="form-builder-container">
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12}>
          <Card
            className="form-card"
            title={
              <Title level={3} className="form-title" style={{color: colors.sidebarText}}>
                Create Your Survey
              </Title>
            }
          >
            <div className="form-name-section">
              <label>Survey Name:</label>
              <Input
                value={formName}
                onChange={handleFormNameChange}
                placeholder="Enter survey name"
                rules={[{ pattern: /^[A-Za-z\s]+$/, message: "Only letters and spaces are allowed" }]}
                className="form-name-input"
              />
            </div>

            <div className="question-type-section">
              <label>Question Type:</label>
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    ?.includes(input?.toLowerCase())
                }
                value={currentQuestion.type}
                onChange={handleQuestionTypeChange}
                className="question-type-select"
                style={{
                  width: "100%",
                  border: "1px solid grey",
                  borderRadius: 5,
                }}
              >
                <Select.Option value="text">Text</Select.Option>
                <Select.Option value="radio">Multiple Choice</Select.Option>
                <Select.Option value="number">Number</Select.Option>
                <Select.Option value="comment">Suggestions</Select.Option>
                <Select.Option value="rating"> Rating</Select.Option>
              </Select>
            </div>
            <div className="question-input-section">
              <label>Question:</label>
              <Input
                value={currentQuestion.question}
                onChange={handleQuestionChange}
                placeholder="Enter your question"
                className="question-input"
              />
            </div>

            {currentQuestion.type === "radio" && (
              <div className="options-section">
                <label>Options:</label>
                {currentQuestion.options.map((option, index) => (
                  <Input
                    key={index}
                    value={option}
                    onChange={(e) => handleOptionChange(e, index)}
                    placeholder={`Option ${index + 1}`}
                    className="option-input"
                  />
                ))}
                <Button
                  icon={<PlusOutlined />}
                  onClick={addOption} // Using the updated addOption function
                  className="add-option-btn"
                >
                  Add Option
                </Button>
              </div>
            )}

            <Button
              type="primary"
              onClick={addQuestion}
              className="add-question-btn"
              style={{background: colors.button}}
            >
              Add Question
            </Button>
            <h2>Form : {formName}</h2>
            <div className="questions-list">
              {questions.map((question, index) =>
                renderQuestion(question, index)
              )}
            </div>

            <Button
              type="primary"
              onClick={handleFormSubmit}
              className="submit-btn"
              style={{background: colors.button}}
            >
              Create Survey
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FormBuilder;
